import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { attendanceService, payrollService, hrService } from '../../lib/checkInAttendanceService';
import Icon from '../../components/AppIcon';
import AttendanceTracker from './components/AttendanceTracker';
import AttendanceHistory from './components/AttendanceHistory';
import PayrollSummary from './components/PayrollSummary';
import LeaveManagement from './components/LeaveManagement';
import RoleBasedHeader from '../../components/ui/RoleBasedHeader';
import DashboardNavigation from '../../components/ui/DashboardNavigation';

const StaffAttendanceTracking = () => {
  const { user, userProfile } = useAuth();
  const [activeTab, setActiveTab] = useState('attendance');
  const [currentAttendance, setCurrentAttendance] = useState(null);
  const [attendanceHistory, setAttendanceHistory] = useState([]);
  const [payrollRecords, setPayrollRecords] = useState([]);
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [navVisible, setNavVisible] = useState(false);

  useEffect(() => {
    if (user?.id) {
      loadData();
      setupRealtimeSync();
    }
  }, [user]);

  const loadData = async () => {
    setLoading(true);
    await Promise.all([
      loadCurrentAttendance(),
      loadAttendanceHistory(),
      loadPayrollRecords(),
      loadLeaveRequests()
    ]);
    setLoading(false);
  };

  const loadCurrentAttendance = async () => {
    const { data } = await attendanceService?.getCurrentAttendance(user?.id);
    setCurrentAttendance(data);
  };

  const loadAttendanceHistory = async () => {
    const { data } = await attendanceService?.getStaffAttendance(user?.id);
    if (data) setAttendanceHistory(data);
  };

  const loadPayrollRecords = async () => {
    const { data } = await payrollService?.getStaffPayroll(user?.id);
    if (data) setPayrollRecords(data);
  };

  const loadLeaveRequests = async () => {
    const { data } = await hrService?.getStaffLeaveRequests(user?.id);
    if (data) setLeaveRequests(data);
  };

  const setupRealtimeSync = () => {
    if (!userProfile?.gym_id) return;

    const channel = attendanceService?.subscribeToAttendance(
      userProfile?.gym_id,
      (payload) => {
        if (payload?.new?.staff_id === user?.id) {
          loadCurrentAttendance();
          loadAttendanceHistory();
        }
      }
    );

    return () => {
      channel?.unsubscribe();
    };
  };

  const handleClockIn = async () => {
    setLoading(true);
    const { data, error } = await attendanceService?.clockIn({
      staffId: user?.id,
      gymId: userProfile?.gym_id,
      location: 'Main Gym'
    });

    if (!error && data) {
      setCurrentAttendance(data);
      await loadAttendanceHistory();
    }
    setLoading(false);
  };

  const handleClockOut = async () => {
    if (!currentAttendance?.id) return;
    setLoading(true);
    const { data, error } = await attendanceService?.clockOut(currentAttendance?.id);

    if (!error && data) {
      setCurrentAttendance(null);
      await loadAttendanceHistory();
    }
    setLoading(false);
  };

  const handleStartBreak = async () => {
    if (!currentAttendance?.id) return;
    setLoading(true);
    const { data, error } = await attendanceService?.startBreak(currentAttendance?.id);

    if (!error && data) {
      setCurrentAttendance(data);
    }
    setLoading(false);
  };

  const handleEndBreak = async () => {
    if (!currentAttendance?.id) return;
    setLoading(true);
    const { data, error } = await attendanceService?.endBreak(currentAttendance?.id);

    if (!error && data) {
      setCurrentAttendance(data);
    }
    setLoading(false);
  };

  const tabs = [
    { id: 'attendance', label: 'Attendance', icon: 'Clock' },
    { id: 'payroll', label: 'Payroll', icon: 'DollarSign' },
    { id: 'leave', label: 'Leave Requests', icon: 'Calendar' }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Hidden Navigation with Mouseover */}
      <div 
        className="fixed top-0 left-0 right-0 z-50 transition-transform duration-300 ease-in-out"
        style={{ transform: navVisible ? 'translateY(0)' : 'translateY(-100%)' }}
        onMouseEnter={() => setNavVisible(true)}
      >
        <RoleBasedHeader />
        <DashboardNavigation />
      </div>

      {/* Hover trigger area */}
      <div 
        className="fixed top-0 left-0 right-0 h-4 z-40"
        onMouseEnter={() => setNavVisible(true)}
        onMouseLeave={() => setNavVisible(false)}
      />

      <div className="py-8 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-foreground mb-2">Staff Attendance Tracking</h1>
            <p className="text-muted-foreground">Manage your time, attendance, and payroll</p>
          </div>

          {/* Tabs */}
          <div className="card mb-6">
            <div className="flex border-b border-border">
              {tabs?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex-1 flex items-center justify-center gap-2 py-4 px-6 font-medium transition-colors ${
                    activeTab === tab?.id
                      ? 'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Icon name={tab?.icon} size={20} />
                  <span>{tab?.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="space-y-6">
            {activeTab === 'attendance' && (
              <>
                <AttendanceTracker
                  currentAttendance={currentAttendance}
                  onClockIn={handleClockIn}
                  onClockOut={handleClockOut}
                  onStartBreak={handleStartBreak}
                  onEndBreak={handleEndBreak}
                  loading={loading}
                />
                <AttendanceHistory
                  attendanceHistory={attendanceHistory}
                  loading={loading}
                  onRefresh={loadAttendanceHistory}
                />
              </>
            )}

            {activeTab === 'payroll' && (
              <PayrollSummary
                payrollRecords={payrollRecords}
                loading={loading}
                onRefresh={loadPayrollRecords}
              />
            )}

            {activeTab === 'leave' && (
              <LeaveManagement
                leaveRequests={leaveRequests}
                loading={loading}
                onRefresh={loadLeaveRequests}
                staffId={user?.id}
                gymId={userProfile?.gym_id}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StaffAttendanceTracking;